// src/pages/SubscriptionManagementPage.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate } from 'react-router-dom'; // 引入 useNavigate and RouterLink
import {
    Container,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Box,
    Paper,
    Grid,
    Link,
} from '@mui/material';

const SubscriptionManagementPage = () => {
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate(); // 初始化 useNavigate

    useEffect(() => {
        const fetchSubscriptionInfo = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('token'); // 从 localStorage 获取 token (如果使用 JWT)
                if (!token) {
                    navigate('/login'); // 未登录跳转到登录页
                    return;
                }
                const response = await axios.get('/subscriptions/me', { //  假设后端接口为 /subscriptions/me
                    headers: {
                        Authorization: `Bearer ${token}`, //  携带 token
                    },
                });
                setSubscriptionInfo(response.data);
                setLoading(false);
            } catch (error) {
                console.error('获取订阅信息失败:', error.response?.data?.message || '获取订阅信息失败');
                setError(error.response?.data?.message || '获取订阅信息失败');
                setLoading(false);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    alert('您的登录状态已过期，请重新登录');
                    navigate('/login');
                }
            }
        };

        fetchSubscriptionInfo();
    }, [navigate]); //  添加 navigate 到依赖数组

    const handleCancelSubscription = async () => {
        if (!window.confirm('您确定要取消订阅吗？')) { //  确认对话框
            return;
        }
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            await axios.post('/subscriptions/cancel', {}, { //  假设后端取消订阅接口为 /subscriptions/cancel
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            alert('订阅已取消。');
            setSubscriptionInfo(prevState => ({ ...prevState, status: 'canceled' })); //  更新前端订阅状态
            setLoading(false);
        } catch (error) {
            console.error('取消订阅失败:', error.response?.data?.message || '取消订阅失败');
            setError(error.response?.data?.message || '取消订阅失败');
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    订阅管理
                </Typography>

                {loading && (
                    <CircularProgress />
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                {!loading && !error && subscriptionInfo ? (
                    <Paper elevation={3} sx={{ p: 3, maxWidth: 400, width: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            当前订阅信息
                        </Typography>
                        <Typography variant="body1" paragraph>
                            套餐名称: {subscriptionInfo.planName}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            订阅状态: {subscriptionInfo.status}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            下次支付日: {subscriptionInfo.nextPaymentDate}
                        </Typography>

                        {subscriptionInfo.status === 'active' && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleCancelSubscription}
                                disabled={loading}
                                sx={{ mt: 2 }}
                            >
                                取消订阅
                            </Button>
                        )}
                        {subscriptionInfo.status === 'canceled' && (
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                您的订阅已取消。
                            </Typography>
                        )}
                    </Paper>
                ) : (!loading && !error && !subscriptionInfo && (
                    <Typography variant="body1" paragraph>
                        您当前没有订阅任何套餐。
                    </Typography>
                ))}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button component={RouterLink} to="/profile" variant="outlined">
                        返回个人资料
                    </Button>
                    <Button component={RouterLink} to="/" variant="outlined">
                        返回首页
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default SubscriptionManagementPage;